"use client";

import { useCallback, useEffect, useState, useTransition } from "react";
import { Icon } from "@/lib/icons/Icon";
import { setThemeAction } from "@/lib/theme/actions";
import { useT } from "@/lib/i18n/context";
import { accessibilityToggleTranslations } from "./AccessibilityToggle.i18n";
import styles from "./AccessibilityToggle.module.css";

/**
 * ♿ AccessibilityToggle — panel dostępności cyfrowej (WCAG) dla ODWIEDZAJĄCEGO
 * stronę publiczną. Renderowany w `HDefault` obok `ThemeToggle`.
 *
 * **Gating:** rozszerzenie `cms_wcag_availability` aktywne dla domeny
 * (`meta.wcag_available === true` w `cmsConfig`). To jest jedyna brama —
 * gdy rozszerzenie włączone, panel pokazuje wszystkie 3 sekcje + skróty
 * klawiszowe. Brak osobnych admin sub-toggle'i.
 *
 * **NIE jest to komponent admin** — mimo że lokalizacja kodu może
 * to sugerować przy szybkim spojrzeniu, panel jest dla każdego odwiedzającego
 * (visitor / klient / admin — bez różnicy).
 *
 * **Architektura efektów:**
 *   - Rozmiar czcionki + podświetlanie linków (client-side):
 *     persistowane w `localStorage` pod kluczami `a11y_font_scale` /
 *     `a11y_link_highlight`. Atrybuty na `document.documentElement`:
 *       - `data-a11y-font-scale="X"` + `style.--a11y-font-scale: X`
 *       - `data-a11y-link-highlight="on"`
 *     Reguły CSS w `app/globals.css` reagują na atrybuty (skalowanie tokenów
 *     `--font-size-*`, underline `<a>`). Inline script w `app/layout.tsx`
 *     aplikuje preferencje przed hydration (zapobiega FOUC).
 *
 *   - Wysoki kontrast (SSR cookie):
 *     Przełącza motyw na `hc` przez `setThemeAction` (server action) —
 *     identyczny mechanizm co `ThemeToggle`. Cookie `cms_theme` ->
 *     `data-theme="hc"` na `<html>` w root layout -> CSS variables
 *     z `templates/{symbol}/theme-hc.css` aktywne. Wspólne źródło prawdy:
 *     odwiedzający w trybie `hc` widzi go zarówno w ThemeToggle jak i
 *     w checkboxie "Wysoki kontrast" w panelu dostępności.
 *
 * **Keyboard shortcuts (Alt+1 / Alt+2):** zarejestrowane globalnie gdy
 * rozszerzenie aktywne. Szukają focusable elementu z atrybutem
 * `data-a11y-target="content"` / `data-a11y-target="menu"`. Brak elementu =
 * shortcut nie robi nic (graceful no-op).
 *
 * @see HDefault.tsx — mount obok `<ThemeToggle />` w `.controls`
 * @see app/globals.css sekcja "WCAG" — reguły CSS reagujące na atrybuty
 * @see app/layout.tsx — pre-hydration script przywracający preferencje
 */

const STORAGE_FONT_SCALE = "a11y_font_scale";
const STORAGE_LINK_HIGHLIGHT = "a11y_link_highlight";

/** Krok 0.1, zakres 0.8..1.5 -> 8 pozycji slidera (Mała ←-> 100% ←-> Duża). */
const FONT_SCALE_MIN = 0.8;
const FONT_SCALE_MAX = 1.5;
const FONT_SCALE_STEP = 0.1;
const FONT_SCALE_DEFAULT = 1.0;

export function AccessibilityToggle() {
  const [open, setOpen] = useState(false);
  const [fontScale, setFontScale] = useState(FONT_SCALE_DEFAULT);
  const [contrast, setContrast] = useState(false);
  const [linkHighlight, setLinkHighlight] = useState(false);
  const [, startTransition] = useTransition();
  const t = useT(accessibilityToggleTranslations);

  /* Initial sync z DOM/localStorage po hydration. Pre-hydration script
     w layout.tsx ustawił `data-a11y-*` na <html>, a Server Component
     (root layout) ustawił `data-theme` (motyw `hc` aktywny gdy cookie
     `cms_theme=hc`). Czytamy oba z DOM dla initial state. */
  useEffect(() => {
    const html = document.documentElement;
    const scaleAttr = html.getAttribute("data-a11y-font-scale");
    if (scaleAttr) {
      const parsed = parseFloat(scaleAttr);
      if (!Number.isNaN(parsed)) setFontScale(parsed);
    }
    setContrast(html.dataset.theme === "hc");
    setLinkHighlight(html.getAttribute("data-a11y-link-highlight") === "on");
  }, []);

  /* Effect helpers — pojedynczy punkt aplikujący zmianę do DOM + localStorage.
     Wywoływane przez handlers; nie jako useEffect żeby zmiana była natychmiastowa
     (bez czekania na re-render i side-effect). */
  const applyFontScale = useCallback((value: number) => {
    const clamped = Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, value));
    const html = document.documentElement;
    if (Math.abs(clamped - FONT_SCALE_DEFAULT) < 0.01) {
      html.removeAttribute("data-a11y-font-scale");
      html.style.removeProperty("--a11y-font-scale");
      try { localStorage.removeItem(STORAGE_FONT_SCALE); } catch {}
    } else {
      html.setAttribute("data-a11y-font-scale", clamped.toFixed(1));
      html.style.setProperty("--a11y-font-scale", clamped.toFixed(2));
      try { localStorage.setItem(STORAGE_FONT_SCALE, clamped.toFixed(1)); } catch {}
    }
    setFontScale(clamped);
  }, []);

  /* "Wysoki kontrast" = przełącz motyw na `hc` / z powrotem na `light`.
     Optimistic update DOM żeby kolor zmienił się natychmiast (tak samo jak
     w ThemeToggle), background persist do cookie przez Server Action.
     Wybór `light` przy wyłączeniu (nie poprzedni motyw) — uproszczona
     UX: jeśli odwiedzający chce wrócić do dark/funeral, użyje ThemeToggle. */
  const applyContrast = useCallback((value: boolean) => {
    const next = value ? "hc" : "light";
    const html = document.documentElement;
    if (next === "light") {
      delete html.dataset.theme;
    } else {
      html.dataset.theme = next;
    }
    setContrast(value);
    startTransition(async () => {
      await setThemeAction(next);
    });
  }, []);

  const applyLinkHighlight = useCallback((value: boolean) => {
    const html = document.documentElement;
    if (value) {
      html.setAttribute("data-a11y-link-highlight", "on");
      try { localStorage.setItem(STORAGE_LINK_HIGHLIGHT, "on"); } catch {}
    } else {
      html.removeAttribute("data-a11y-link-highlight");
      try { localStorage.removeItem(STORAGE_LINK_HIGHLIGHT); } catch {}
    }
    setLinkHighlight(value);
  }, []);

  const handleReset = useCallback(() => {
    applyFontScale(FONT_SCALE_DEFAULT);
    /* `applyContrast(false)` przełączy motyw na `light`, więc nawet gdy
       odwiedzający miał wcześniej `dark`/`funeral` (ThemeToggle), reset
       wraca do baseline. Świadome uproszczenie — alternatywą byłoby
       wracać do `default_cms_theme` z cmsConfig, ale to dodatkowa
       złożoność dla minimalnego zysku. */
    applyContrast(false);
    applyLinkHighlight(false);
  }, [applyFontScale, applyContrast, applyLinkHighlight]);

  /* Keyboard shortcuts (Alt+1 / Alt+2) — działają niezależnie od stanu panelu,
     rejestrowane póki komponent zamontowany (ext aktywne).

     **Dlaczego `e.code` zamiast `e.key`:** na macOS Option+cyfra produkuje
     specjalny znak (Option+1 = "¡", Option+2 = "™") — `e.key` zwraca ten znak,
     nie cyfrę, więc proste `e.key === "1"` failowało na Mac. `e.code` reprezentuje
     fizyczny klawisz niezależnie od układu i modyfikatorów: `"Digit1"`/`"Digit2"`
     dla cyfr na rzędzie nad literami, `"Numpad1"`/`"Numpad2"` dla numpadu —
     łapiemy oba żeby działało też z keypadu.

     **Selektor menu:** najpierw szukamy explicit `[data-a11y-target="menu"]`
     (admin może oznaczyć dowolny element), fallback do pierwszego `<nav>`
     na stronie (WMenu / WDropDownMenu renderują nav element). Brak menu = no-op. */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

      const isDigit1 = e.code === "Digit1" || e.code === "Numpad1";
      const isDigit2 = e.code === "Digit2" || e.code === "Numpad2";
      if (!isDigit1 && !isDigit2) return;

      let el: HTMLElement | null = null;
      if (isDigit1) {
        el = document.querySelector<HTMLElement>('[data-a11y-target="content"]');
      } else {
        el = document.querySelector<HTMLElement>('[data-a11y-target="menu"]')
          ?? document.querySelector<HTMLElement>('nav');
      }
      if (!el) return;

      e.preventDefault();
      /* `<nav>` bez tabIndex nie jest focusowalny — dodajemy tabIndex=-1
         tymczasowo żeby .focus() zadziałał (programmatic focus, nie tab order). */
      if (!el.hasAttribute("tabindex")) {
        el.setAttribute("tabindex", "-1");
      }
      el.focus();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  /* Esc zamyka panel — analogicznie do modal pattern. Listener dodajemy tylko
     gdy panel open, żeby nie blokował innych Esc handlerów (np. modals admin). */
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setOpen((v) => !v)}
        aria-label={t.triggerAria}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={t.triggerTitle}
      >
        <Icon name="person-arms-spread" className={styles.icon} weight="bold" />
      </button>

      {open && (
        <>
          {/* Backdrop — klik poza panelem zamyka. */}
          <div
            className={styles.backdrop}
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className={styles.panel}
            role="dialog"
            aria-label={t.dialogAria}
          >
            <header className={styles.header}>
              <h2 className={styles.title}>{t.panelTitle}</h2>
              <button
                type="button"
                className={styles.close}
                onClick={() => setOpen(false)}
                aria-label={t.closeAria}
              >
                <Icon name="x" />
              </button>
            </header>

            <div className={styles.body}>
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Icon name="text-aa" className={styles.sectionIcon} />
                  {t.fontSize.heading}
                </h3>
                <input
                  type="range"
                  min={FONT_SCALE_MIN}
                  max={FONT_SCALE_MAX}
                  step={FONT_SCALE_STEP}
                  value={fontScale}
                  onChange={(e) => applyFontScale(parseFloat(e.target.value))}
                  className={styles.slider}
                  aria-label={t.fontSize.sliderAria}
                />
                <div className={styles.sliderLabels}>
                  <span>{t.fontSize.small}</span>
                  <span>{Math.round(fontScale * 100)}%</span>
                  <span>{t.fontSize.large}</span>
                </div>
              </section>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Icon name="circle-half" className={styles.sectionIcon} />
                  {t.contrast.heading}
                </h3>
                <label className={styles.toggleRow}>
                  <span>{t.contrast.label}</span>
                  <input
                    type="checkbox"
                    checked={contrast}
                    onChange={(e) => applyContrast(e.target.checked)}
                    className={styles.checkbox}
                  />
                </label>
              </section>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Icon name="link" className={styles.sectionIcon} />
                  {t.navigation.heading}
                </h3>
                <label className={styles.toggleRow}>
                  <span>{t.navigation.highlightLinks}</span>
                  <input
                    type="checkbox"
                    checked={linkHighlight}
                    onChange={(e) => applyLinkHighlight(e.target.checked)}
                    className={styles.checkbox}
                  />
                </label>
              </section>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>{t.shortcuts.heading}</h3>
                <div className={styles.shortcutRow}>
                  <span>{t.shortcuts.jumpToContent}</span>
                  <kbd className={styles.kbd}>Alt + 1</kbd>
                </div>
                <div className={styles.shortcutRow}>
                  <span>{t.shortcuts.jumpToMenu}</span>
                  <kbd className={styles.kbd}>Alt + 2</kbd>
                </div>
              </section>
            </div>

            <footer className={styles.footer}>
              <button
                type="button"
                className={styles.resetButton}
                onClick={handleReset}
              >
                <Icon name="arrow-clockwise" className={styles.resetIcon} />
                {t.reset}
              </button>
            </footer>
          </div>
        </>
      )}
    </>
  );
}
